import { useEffect } from "react";
import { useRecoilCallback } from "recoil";

import {
  consumeKeepEditFormDtoOnUnmount,
  editFormDataAF,
  editFormDataInitialSnapshotAF,
  EditFormMode,
  getEditFormCacheAtomKey,
  loadingEditFormContentAtomFamily,
  Log,
  RydeUiUnit,
} from "@ryde/shared";

/**
 * On close of an edit card, drop the DTO after unmount so reopen fetches.
 * Skip while `id` is still set (that would refetch on the closing card).
 * Add/duplicate/reverse instances are not cleared here — KEEP covers edit→add
 * when the processed dto must survive the source unmount.
 */
export const useEditFormCacheOnUnmount = ({
  entity,
  mode,
  id,
  idCopyFrom,
}: {
  entity: RydeUiUnit;
  mode?: EditFormMode;
  id?: number;
  idCopyFrom?: number;
}): void => {
  const isEditInstance = Boolean(id) && !idCopyFrom;

  const clearEditFormCacheUnlessKept = useRecoilCallback(
    ({ set }) =>
      () => {
        const key = getEditFormCacheAtomKey({ entity, mode });
        if (!isEditInstance) {
          return;
        }
        if (consumeKeepEditFormDtoOnUnmount(key)) {
          Log.debug(
            `Keep edit-form dto on unmount entity=${entity} mode=${key[1]}`,
          );
          return;
        }
        set(editFormDataAF(key), undefined);
        set(editFormDataInitialSnapshotAF(key), undefined);
        set(loadingEditFormContentAtomFamily(entity), false);
        Log.debug(
          `Invalidated edit-form dto cache on unmount entity=${entity} mode=${key[1]}`,
        );
      },
    [entity, id, idCopyFrom, isEditInstance, mode],
  );

  useEffect(() => {
    consumeKeepEditFormDtoOnUnmount(getEditFormCacheAtomKey({ entity, mode }));
  }, [entity, mode]);

  useEffect(() => {
    return clearEditFormCacheUnlessKept;
  }, [clearEditFormCacheUnlessKept]);
};
